import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Input, Button } from 'antd';
import { MessageOutlined, SendOutlined } from '@ant-design/icons';
import { THEME, MAX_TEXT_LENGTH } from './EscalationModal';
import { ESCALATION_TYPES, resolvedLabelFor } from './escalationTypes';
import { DateTimeFormater } from '../../../services/Helper';

const { TextArea } = Input;

// Timeline of one escalation (raised, replies, resolved, reopened) with a reply
// box. onReply(text) resolves to { success, message } and the parent passes the
// updated escalation back in, so the thread stays open after each reply.
const EscalationActivityModal = ({ open, escalation, orderRef, loading, onCancel, onReply }) => {
  const [text, setText] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setText('');
      setError(null);
    }
  }, [open]);

  const config = escalation && ESCALATION_TYPES[escalation.type];
  const actionLabels = {
    ESCALATED: 'Escalated',
    REPLY: 'Reply',
    RESOLVED: config ? resolvedLabelFor(config) : 'Resolved',
    REOPENED: 'Reopened',
  };
  const activity = escalation?.activity || [];

  const handleSend = async () => {
    const response = await onReply(text.trim());
    if (response?.success) {
      setText('');
      setError(null);
    } else {
      setError(response?.message || 'Failed to add reply');
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MessageOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>Escalation Activity</div>
            {orderRef && <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{orderRef}</div>}
          </div>
        </div>
      }
      open={open}
      onCancel={loading ? undefined : onCancel}
      footer={null}
      width={560}
      destroyOnClose
    >
      <div style={{ maxHeight: 360, overflowY: 'auto', margin: '4px 0 16px' }}>
        {activity.length === 0 && (
          <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No activity yet.</p>
        )}
        {activity.map((entry) => (
          <div
            key={entry._id}
            style={{ borderLeft: `3px solid ${THEME.border}`, background: THEME.light, borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}
          >
            <div style={{ fontSize: 12, color: '#666', marginBottom: entry.text ? 4 : 0 }}>
              <strong style={{ color: THEME.primary }}>{actionLabels[entry.action] || entry.action}</strong>
              {' · '}{entry.by}{' · '}{DateTimeFormater(entry.at)}
            </div>
            {entry.text && <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{entry.text}</div>}
          </div>
        ))}
      </div>
      <TextArea
        rows={3}
        value={text}
        maxLength={MAX_TEXT_LENGTH}
        showCount
        placeholder="Write a reply..."
        disabled={loading}
        onChange={(e) => setText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {error && <div style={{ color: '#cf1322', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <Button
        type="primary"
        block
        icon={<SendOutlined />}
        loading={loading}
        disabled={!text.trim()}
        onClick={handleSend}
        style={text.trim() ? { background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 40 } : { fontWeight: 600, height: 40 }}
      >
        Send Reply
      </Button>
    </Modal>
  );
};

EscalationActivityModal.propTypes = {
  open: PropTypes.bool.isRequired,
  escalation: PropTypes.object,
  orderRef: PropTypes.string,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onReply: PropTypes.func.isRequired,
};

export default EscalationActivityModal;
